// React error boundary so render-time crashes show a real error
// message instead of unmounting silently into a blank page.
//
// The render-error panel is the template's destructive alert
// (components/ui/alert.tsx). It sits outside `.helix` in both apps, so it
// reads only :root tokens and Tailwind utilities, never an hx class.
// Record: docs/testing/ui-migration/070-error-boundary.md.

import { Component, type ErrorInfo, type ReactNode } from "react";
import { isChunkLoadError } from "../lib/chunk-recovery";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Button } from "./ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, componentStack: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ error, componentStack: info.componentStack ?? null });
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  render(): ReactNode {
    // Last line of defence. lazyPage keeps a chunk failure from reaching the
    // boundary at all (it shows a toast instead); if one still does, say a new
    // version is out and offer the reload, not a component stack.
    if (this.state.error && isChunkLoadError(this.state.error)) {
      return (
        <div
          className="m-8 p-8 font-sans text-sm/[1.6] text-muted-foreground"
          role="status"
          aria-live="polite"
        >
          <strong className="mb-1 block text-[15px] text-foreground">
            A new version is available
          </strong>
          Helix was updated while this tab was open. Reload to get the latest changes.
          <Button type="button" size="sm" className="mt-3 block" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </div>
      );
    }
    if (this.state.error) {
      return (
        <Alert variant="destructive" className="m-8 w-auto">
          <AlertTitle>Render error</AlertTitle>
          <AlertDescription className="font-mono text-xs/[1.6] whitespace-pre-wrap text-pretty">
            {this.state.error.name}: {this.state.error.message}
            {this.state.error.stack && (
              <>
                {"\n\n"}
                {this.state.error.stack.split("\n").slice(0, 8).join("\n")}
              </>
            )}
            {this.state.componentStack && (
              <>
                {"\n\nComponent stack:"}
                {this.state.componentStack.split("\n").slice(0, 12).join("\n")}
              </>
            )}
          </AlertDescription>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2 justify-self-start"
            onClick={() => this.setState({ error: null, componentStack: null })}
          >
            Dismiss
          </Button>
        </Alert>
      );
    }
    return this.props.children;
  }
}
